import styled from '@emotion/styled';
import { Card, rem } from '@triumph/design-system';

const CARD_BORDER = (theme: any) =>
  `${rem(1)} solid ${theme.colors.greyscale.gray15}`;

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${rem(880)};
  margin: 0 auto;
`;

export const BackLink = styled.button`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: ${rem(8)};
  background: none;
  border: none;
  padding: 0 0 ${rem(16)} 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};

  &:hover {
    color: ${({ theme }) => theme.colors.greyscale.gray50};
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-family: 'Gotham';
  font-size: ${rem(26)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const PageDescription = styled.div`
  padding: ${rem(6)} 0 ${rem(24)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(16)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
  max-width: ${rem(640)};
`;

export const PreferenceCard = styled(Card)`
  border: ${({ theme }) => CARD_BORDER(theme)};
  border-radius: ${rem(12)};
  min-height: 0;
  overflow: hidden;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(16)};
  padding: ${rem(20)} ${rem(24)};
`;

export const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(8)};
  background: ${({ theme }) => theme.colors.greyscale.gray5};
`;

export const CardHeaderBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  flex: 1;
  min-width: 0;
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
`;

export const CardTitle = styled.div`
  font-family: 'Gotham';
  font-size: ${rem(16)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const CardSubtitle = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const ToggleControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  flex-shrink: 0;
`;

export const ToggleLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const InfoBanner = styled.div<{ variant?: 'info' | 'warning' }>`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  padding: ${rem(12)} ${rem(24)};
  border-top: ${({ theme }) => CARD_BORDER(theme)};
  border-bottom: ${({ theme }) => CARD_BORDER(theme)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(14)};
  background: ${({ theme, variant }) =>
    variant === 'warning'
      ? theme.colors.semantic.warning10
      : theme.colors.secondary.actionHoverSecondary};
  color: ${({ theme, variant }) =>
    variant === 'warning'
      ? theme.colors.semantic.warningDefault
      : theme.colors.primary.actionDefault};
`;

export const CardBody = styled.div`
  padding: ${rem(20)} ${rem(24)} ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`;

export const SectionHeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding-bottom: ${rem(12)};
`;

export const SectionHeadingText = styled.span`
  font-family: 'Gotham';
  font-size: ${rem(15)};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const DestinationRowContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(8)};
  margin-bottom: ${rem(8)};
`;

export const DestinationInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

export const AddButtonRow = styled.div`
  padding-top: ${rem(4)};
`;

export const HelpText = styled.div`
  padding-top: ${rem(8)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${rem(16)};

  @media (max-width: ${rem(640)}) {
    grid-template-columns: 1fr;
  }
`;

export const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  padding-bottom: ${rem(6)};
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(13)};
  color: ${({ theme }) => theme.colors.greyscale.gray50};
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem(16)};
  padding-top: ${rem(4)};
`;

export const EffectNote = styled.span`
  font-family: ${({ theme }) => theme.typography.fontFamilies.primary};
  font-size: ${rem(12)};
  color: ${({ theme }) => theme.colors.greyscale.gray40};
`;
